import React, { useEffect, useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchProductFunctions, deleteProductFunction } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import DetailSection from "../../components/common/DetailSection";
import DetailField from "../../components/common/DetailField";
import ConfirmDialog from "../../components/common/ConfirmDialog";

export default function ProductFunctionDetailPage() {
  const { businessDomainId, businessUnitId, categoryId, functionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [data, setData] = useState(location.state?.functionData || null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const basePath = `/strategy/${businessDomainId}/business-units/${businessUnitId}/product-categories/${categoryId}`;

  // fallback: fetch from list if data not in state
  useEffect(() => {
    if (!data) {
      (async () => {
        try {
          const funcs = await fetchProductFunctions(categoryId);
          const found = (funcs || []).find((f) => f.productFunctionId === functionId);
          if (found) setData(found);
        } catch (e) { notify.apiError(e); }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [functionId]);

  const handleDelete = async () => {
    try {
      await deleteProductFunction(functionId);
      notify.success(t("message.delete.success"));
      navigate(`${basePath}/product-functions`, { replace: true });
    } catch (e) { notify.apiError(e); }
    setConfirmOpen(false);
  };

  if (!data) return null;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("productFunction.detail.title")}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(
              `${basePath}/product-functions/${functionId}/edit`,
              { state: { functionData: data } }
            )}
          >
            {t("common.edit")}
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setConfirmOpen(true)}
          >
            {t("common.delete")}
          </Button>
        </Box>
      </Box>

      <DetailSection>
        <DetailField label={t("productFunction.productTask")} value={data.productTaskName} />
        <DetailField label={t("productFunction.name")} value={data.productFunctionName} />
        <DetailField label={t("productFunction.description")} value={data.productFunctionDescription} />
        <DetailField label={t("productFunction.function")} value={data.functionName} />
        <DetailField label={t("common.url")} value={data.url} />
      </DetailSection>

      <Box sx={{ mt: 3 }}>
        <Button variant="outlined" onClick={() => navigate(`${basePath}/product-functions`)}>
          {t("common.back")}
        </Button>
      </Box>

      <ConfirmDialog
        open={confirmOpen}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </Box>
  );
}
